import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useIngestionJobs } from '@/hooks/useIngestion';
import { useSources } from '@/hooks/useSources';
import { useDatasets } from '@/hooks/useDatasets';
import { SkeletonPage, SkeletonListItem } from '@/components/skeletons/SkeletonLoaders';
import { ErrorState } from '@/components/EmptyState';
import type { IngestionJob, PaginatedResponse } from '@/types';

interface StatCardProps {
  title: string;
  value: string | number;
  description: string;
  trend?: {
    value: number;
    label: string;
    isPositive: boolean;
  };
}

const StatCard = ({ title, value, description, trend }: StatCardProps) => (
  <Card>
    <CardHeader className="pb-2">
      <CardDescription>{title}</CardDescription>
    </CardHeader>
    <CardContent>
      <div className="text-3xl font-bold">{value}</div>
      <p className="text-xs text-muted-foreground mt-1">{description}</p>
      {trend && (
        <p className={`text-xs mt-2 ${trend.isPositive ? 'text-success' : 'text-destructive'}`}>
          {trend.isPositive ? '+' : ''}{trend.value}% {trend.label}
        </p>
      )}
    </CardContent>
  </Card>
);

const IngestionJobItem = ({ job }: { job: IngestionJob }) => (
  <div className="flex items-center justify-between border-b pb-2 last:border-0">
    <div className="text-sm flex-1 min-w-0">
      <div className="font-medium truncate">{job.filename || 'Unnamed job'}</div>
      <div className="text-xs text-muted-foreground">
        {job.created_at ? new Date(job.created_at).toLocaleString() : 'N/A'}
      </div>
    </div>
    <div>
      <span
        className={`text-xs px-2 py-1 rounded whitespace-nowrap ${
          job.status === 'completed'
            ? 'bg-success/10 text-success'
            : job.status === 'processing'
            ? 'bg-info/10 text-info'
            : job.status === 'failed'
            ? 'bg-destructive/10 text-destructive'
            : 'bg-muted text-muted-foreground'
        }`}
      >
        {job.status}
      </span>
    </div>
  </div>
);

export const Dashboard = () => {
  const { data: jobs, isLoading: jobsLoading, error: jobsError } = useIngestionJobs({ limit: 10 });
  const { data: sources, isLoading: sourcesLoading, error: sourcesError } = useSources();
  // Only `total` is rendered, so ask for the smallest page the API will return
  // rather than a default page of 20 unused rows (#1026).
  const {
    data: datasets,
    isLoading: datasetsLoading,
    error: datasetsError,
  } = useDatasets({ page: 1, size: 1 });

  // Show loading skeleton while any data is loading
  if (jobsLoading || sourcesLoading || datasetsLoading) {
    return <SkeletonPage />;
  }

  // Show error if any request failed
  const error = jobsError || sourcesError || datasetsError;
  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground">
            Overview of your data lakehouse activity
          </p>
        </div>
        <ErrorState
          message={error instanceof Error ? error.message : 'Failed to load dashboard data'}
          onRetry={() => window.location.reload()}
        />
      </div>
    );
  }

  const typedJobs = jobs as PaginatedResponse<IngestionJob> | undefined;
  const activeIngestions = typedJobs?.items?.filter(j => j.status === 'processing').length || 0;

  const stats: StatCardProps[] = [
    {
      title: 'Total Sources',
      value: sources?.total || 0,
      description: 'Connected data sources',
    },
    {
      title: 'Total Datasets',
      value: datasets?.total || 0,
      description: 'Available datasets',
    },
    {
      title: 'Active Ingestions',
      value: activeIngestions,
      description: 'Currently processing',
    },
    {
      title: 'Recent Jobs',
      value: typedJobs?.total || 0,
      description: 'Last 24 hours',
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Dashboard</h1>
        <p className="text-muted-foreground">
          Overview of your data lakehouse activity
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <StatCard key={stat.title} {...stat} />
        ))}
      </div>

      {/* Recent Activity */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Recent Ingestion Jobs</CardTitle>
            <CardDescription>Latest data ingestion activity</CardDescription>
          </CardHeader>
          <CardContent>
            {jobsLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <SkeletonListItem key={i} />
                ))}
              </div>
            ) : typedJobs?.items && typedJobs.items.length > 0 ? (
              <div className="space-y-2">
                {typedJobs.items.slice(0, 5).map((job) => (
                  <IngestionJobItem key={job.id} job={job} />
                ))}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground text-center py-8">
                No recent ingestion jobs
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>System Health</CardTitle>
            <CardDescription>Current system status</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm">API Server</span>
                <span className="text-xs bg-success/10 text-success px-2 py-1 rounded">
                  Healthy
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Database</span>
                <span className="text-xs bg-success/10 text-success px-2 py-1 rounded">
                  Connected
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Storage</span>
                <span className="text-xs bg-success/10 text-success px-2 py-1 rounded">
                  Available
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
